import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';

// Öffentliche Seite einer Veranstaltung aus der events-Tabelle
// (/veranstaltung/:id, alte Links /camp/:id).
//
// ⚠️ Bis 27.09.2026 bekam JEDE Veranstaltung hier das Visa-Formular für Camps:
// wer sich von der Startseite zum Netzwerktreffen in Vilsbiburg anmelden
// wollte, sollte Reisepassnummer und Geburtsort angeben — ohne
// Datenschutzhinweis. Jetzt entscheidet events.typ:
//   Camp            → Passdaten für den Einladungsbrief (zweisprachig, Team Ghana + DE)
//   Netzwerktreffen → nur Infos; Teilnahme über persönliche Einladung (/einladung/:token)
//   alle anderen    → schlankes Formular: Name, E-Mail, Telefon optional
// Der Server prüft dasselbe (eventsController.createEventAnmeldung).

const C = { bg: '#0b0c10', card: '#15162c', red: '#c0392b', gold: '#f0c14b', border: 'rgba(255,255,255,0.12)', text: '#f2f2f6', muted: 'rgba(255,255,255,0.55)' };

const DATENSCHUTZ_URL = 'https://www.tda-intl.com/datenschutz';

const fmt = (d) => { try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return d; } };

const S = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'system-ui, Arial, sans-serif' },
  wrap: { maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem 3rem' },
  back: { color: C.muted, fontSize: '0.9rem', textDecoration: 'none' },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.5rem', marginTop: '1.25rem' },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: '0.75rem 0 0', overflowWrap: 'anywhere' },
  meta: { color: C.muted, marginTop: '0.4rem', fontSize: '0.95rem', lineHeight: 1.5 },
  beschreibung: { whiteSpace: 'pre-line', lineHeight: 1.6, margin: 0, overflowWrap: 'anywhere' },
  // auto-fit: am Handy eine Spalte, sonst zwei — vorher fest zweispaltig, die
  // Passzeile ragte bei 375 px über die Karte hinaus.
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '0.85rem', marginTop: '0.5rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 0 },
  full: { gridColumn: '1 / -1' },
  label: { fontSize: '0.8rem', color: C.muted },
  input: { padding: '0.6rem 0.7rem', borderRadius: 9, border: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.3)', color: C.text, fontSize: '0.95rem', width: '100%', boxSizing: 'border-box', minHeight: '2.6rem' },
  section: { marginTop: '1.25rem', fontWeight: 700, color: C.gold, fontSize: '0.9rem', borderTop: `1px solid ${C.border}`, paddingTop: '1rem' },
  einwilligung: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginTop: '1.25rem', fontSize: '0.88rem', color: C.muted, lineHeight: 1.5 },
  btn: { marginTop: '1.4rem', width: '100%', padding: '0.85rem', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg, #f7d98f, #d9aa43)', color: '#0b0c10', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' },
  err: { marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: `1px solid ${C.red}`, borderRadius: 9, padding: '0.7rem 0.9rem', color: '#ffb4a8' },
  hinweis: { background: 'rgba(240,193,75,0.08)', border: '1px solid rgba(240,193,75,0.35)', borderRadius: 12, padding: '1rem 1.1rem', lineHeight: 1.55 },
  link: { color: C.gold },
  // Für Menschen unsichtbar, für Formular-Bots nicht (Honeypot)
  falle: { position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' },
};

export default function CampRegistrationPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [regResult, setRegResult] = useState(null);   // { id, token } nach Anmeldung
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/events/public')
      .then(r => r.json())
      .then(d => { const e = (d.data || []).find(x => String(x.event_id) === String(id)); setEvent(e || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (event) document.title = `${event.name} · TDA International`;
  }, [event]);

  const istCamp = event?.typ === 'Camp';
  const nurEinladung = event?.typ === 'Netzwerktreffen';
  const offen = event?.status === 'Aktiv'
    && !(event?.anmeldeschluss && new Date(event.anmeldeschluss) < new Date());

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const fld = (k, label, { type = 'text', full = false, required = false, autoComplete } = {}) => (
    <div style={{ ...S.field, ...(full ? S.full : {}) }} key={k}>
      <label style={S.label} htmlFor={`f-${k}`}>{label}{required ? ' *' : ''}</label>
      <input style={S.input} id={`f-${k}`} name={k} type={type} value={form[k] || ''} onChange={set(k)}
        required={required} autoComplete={autoComplete} />
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/events/${id}/anmeldungen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, datenschutz: form.datenschutz === true }),
      });
      const data = await res.json();
      if (data.success) { setRegResult(data.data || null); setDone(true); window.scrollTo(0, 0); }
      else setError(data.error || (istCamp ? 'Registration failed / Anmeldung fehlgeschlagen.' : 'Anmeldung fehlgeschlagen.'));
    } catch (_) { setError(istCamp ? 'Network error / Netzwerkfehler.' : 'Keine Verbindung zum Server.'); }
    finally { setSubmitting(false); }
  };

  const einwilligung = (
    <label style={S.einwilligung} htmlFor="f-datenschutz">
      <input type="checkbox" id="f-datenschutz" name="datenschutz" checked={form.datenschutz === true}
        onChange={set('datenschutz')} required style={{ marginTop: 3, width: 16, height: 16, accentColor: C.gold, flexShrink: 0 }} />
      <span>
        {istCamp && <>I agree to the processing of my data for this registration and the invitation letter. / </>}
        Ich stimme der Verarbeitung meiner Angaben zur Bearbeitung dieser Anmeldung zu
        {istCamp ? ' und zur Erstellung des Einladungsschreibens' : ''}. Details in der{' '}
        <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener noreferrer" style={S.link}>Datenschutzerklärung</a>. *
      </span>
    </label>
  );

  const falle = (
    <div style={S.falle} aria-hidden="true">
      <label htmlFor="f-website">Website</label>
      <input id="f-website" name="website" tabIndex={-1} autoComplete="off" value={form.website || ''} onChange={set('website')} />
    </div>
  );

  let inhalt;
  if (loading) {
    inhalt = <p style={{ color: C.muted }}>Lädt…</p>;
  } else if (!event) {
    // Vorher stand hier für jede Nummer ein vollständiges Formular „Training Camp".
    inhalt = (
      <div style={S.card}>
        <h1 style={{ ...S.h1, marginTop: 0 }}>Veranstaltung nicht gefunden</h1>
        <p style={S.meta}>Die Veranstaltung gibt es nicht oder sie ist bereits vorbei.</p>
        <Link to="/" style={S.link}>Alle Veranstaltungen ansehen</Link>
      </div>
    );
  } else {
    inhalt = (
      <>
        <h1 style={S.h1}>{event.name}</h1>
        <div style={S.meta}>
          📅 {fmt(event.datum)}{event.end_datum && event.end_datum !== event.datum ? ` – ${fmt(event.end_datum)}` : ''}
          {event.ort ? <> · 📍 {event.ort}</> : null}
          {event.anmeldeschluss ? <><br />⏰ Anmeldeschluss: {fmt(event.anmeldeschluss)}</> : null}
          {Number(event.preis) > 0 ? <><br />💶 {Number(event.preis).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</> : null}
        </div>

        {event.beschreibung && (
          <div style={S.card}><p style={S.beschreibung}>{event.beschreibung}</p></div>
        )}

        {nurEinladung ? (
          <div style={{ ...S.card, ...S.hinweis }}>
            <strong>Teilnahme auf Einladung</strong><br />
            Die Gäste haben per E-Mail einen persönlichen Link bekommen. Darüber sagst du zu oder ab.
            Keine Einladung, aber Interesse? Schreib uns an{' '}
            <a href="mailto:info@tda-intl.com" style={S.link}>info@tda-intl.com</a>.
          </div>
        ) : !offen ? (
          <div style={{ ...S.card, ...S.hinweis }}>Für diese Veranstaltung ist keine Anmeldung mehr möglich.</div>
        ) : done ? (
          <div style={S.card}>
            {istCamp ? (
              <>
                <h2 style={{ marginTop: 0, color: C.gold }}>✅ Thank you! / Vielen Dank!</h2>
                <p>Your registration has been received. You can download your official invitation letter for the visa application right now.</p>
                <p style={{ color: C.muted }}>Deine Anmeldung ist eingegangen. Du kannst dein offizielles Einladungsschreiben für den Visumantrag jetzt direkt herunterladen.</p>
                {regResult?.token && (
                  <a style={{ ...S.btn, display: 'block', textAlign: 'center', textDecoration: 'none' }}
                     href={`/api/events/${id}/visa/brief-public/${regResult.id}.pdf?token=${regResult.token}`}
                     target="_blank" rel="noreferrer">
                    ⬇︎ Download invitation letter (PDF) · Einladungsbrief herunterladen
                  </a>
                )}
              </>
            ) : (
              <>
                <h2 style={{ marginTop: 0, color: C.gold }}>✅ Vielen Dank!</h2>
                <p>
                  Deine Anmeldung ist eingegangen
                  {regResult?.status === 'Warteliste'
                    ? '. Die Plätze sind belegt, du stehst auf der Warteliste. Wird ein Platz frei, rückst du automatisch nach.'
                    : '.'}
                </p>
                {/* Wohin zahlen? Stand bis 27.09.2026 nirgends (Server: eventMails.zahlungFuer) */}
                {regResult?.zahlung && (
                  <div style={{ ...S.hinweis, marginTop: '1rem' }}>
                    <strong>Teilnahmegebühr: {Number(regResult.zahlung.betrag).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</strong>
                    {regResult.zahlung.bank ? (
                      <>
                        <br />Bitte überweisen an {regResult.zahlung.bank.kontoinhaber}
                        <br />IBAN: {regResult.zahlung.bank.iban}
                        {regResult.zahlung.bank.bic && <><br />BIC: {regResult.zahlung.bank.bic}</>}
                        <br />Verwendungszweck: <strong>{regResult.zahlung.verwendungszweck}</strong>
                      </>
                    ) : (
                      <><br />Die Zahlungsangaben bekommst du gesondert.</>
                    )}
                  </div>
                )}
                {regResult?.kontakt && (
                  <p style={{ color: C.muted, fontSize: '0.9rem' }}>
                    Du kannst doch nicht? Sag kurz Bescheid: <a href={`mailto:${regResult.kontakt}`} style={S.link}>{regResult.kontakt}</a>
                  </p>
                )}
              </>
            )}
            <button style={{ ...S.btn, marginTop: '0.8rem', background: 'rgba(255,255,255,0.08)', color: C.text }}
                    onClick={() => { setForm({}); setRegResult(null); setDone(false); }}>
              {istCamp ? 'Register another person / Weitere Person anmelden' : 'Weitere Person anmelden'}
            </button>
          </div>
        ) : istCamp ? (
          <form style={S.card} onSubmit={submit}>
            <p style={{ marginTop: 0, color: C.muted, fontSize: '0.9rem' }}>Please fill in exactly as written in the passport. / Bitte genau wie im Reisepass angeben.</p>
            {falle}
            <div style={S.grid}>
              {fld('vorname', 'First name / Vorname', { required: true, autoComplete: 'given-name' })}
              {fld('nachname', 'Surname / Nachname', { required: true, autoComplete: 'family-name' })}
              <div style={S.field}>
                <label style={S.label} htmlFor="f-geschlecht">Gender / Geschlecht *</label>
                <select style={S.input} id="f-geschlecht" name="geschlecht" value={form.geschlecht || ''} onChange={set('geschlecht')} required autoComplete="sex">
                  <option value="">—</option>
                  <option value="männlich">male / männlich</option>
                  <option value="weiblich">female / weiblich</option>
                  <option value="divers">diverse / divers</option>
                </select>
              </div>
              {fld('geburtsdatum', 'Date of birth / Geburtsdatum', { type: 'date', required: true, autoComplete: 'bday' })}
              {fld('geburtsort', 'Place of birth / Geburtsort', { required: true })}
              {fld('geburtsland', 'Country of birth / Geburtsland', { required: true })}
              {fld('nationalitaet', 'Nationality / Staatsangehörigkeit', { required: true })}
            </div>

            <div style={S.section}>Passport / Reisepass</div>
            <div style={S.grid}>
              {fld('pass_nummer', 'Passport No. / Reisepass-Nr.', { required: true })}
              {fld('pass_ausstellungsort', 'Place of issue / Ausstellungsort', { required: true })}
              {fld('pass_ausstellungsdatum', 'Date of issue / Ausgestellt am', { type: 'date', required: true })}
              {fld('pass_ablaufdatum', 'Valid until / Gültig bis', { type: 'date', required: true })}
            </div>

            <div style={S.section}>Home address / Wohnanschrift</div>
            <div style={S.grid}>
              {fld('adresse_strasse', 'Street & no. / Straße & Nr.', { full: true, required: true, autoComplete: 'street-address' })}
              {fld('adresse_plz', 'Postal code / PLZ', { autoComplete: 'postal-code' })}
              {fld('adresse_ort', 'City / Ort', { required: true, autoComplete: 'address-level2' })}
              {fld('adresse_land', 'Country / Land', { required: true, autoComplete: 'country-name' })}
            </div>

            <div style={S.section}>Contact / Kontakt</div>
            <div style={S.grid}>
              {fld('email', 'Email (optional)', { type: 'email', autoComplete: 'email' })}
              {fld('telefon', 'Phone / Telefon (optional)', { type: 'tel', autoComplete: 'tel' })}
            </div>

            {einwilligung}
            {error && <div style={S.err}>{error}</div>}
            <button style={S.btn} type="submit" disabled={submitting}>{submitting ? 'Sending… / Senden…' : 'Submit registration / Anmeldung absenden'}</button>
          </form>
        ) : (
          <form style={S.card} onSubmit={submit}>
            {falle}
            <div style={S.grid}>
              {fld('vorname', 'Vorname', { required: true, autoComplete: 'given-name' })}
              {fld('nachname', 'Nachname', { required: true, autoComplete: 'family-name' })}
              {fld('email', 'E-Mail', { type: 'email', required: true, autoComplete: 'email' })}
              {fld('telefon', 'Telefon (optional)', { type: 'tel', autoComplete: 'tel' })}
            </div>
            {einwilligung}
            {error && <div style={S.err}>{error}</div>}
            <button style={S.btn} type="submit" disabled={submitting}>{submitting ? 'Wird gesendet…' : 'Anmeldung absenden'}</button>
            <p style={{ color: C.muted, fontSize: '0.8rem', marginBottom: 0 }}>* Pflichtfelder</p>
          </form>
        )}
      </>
    );
  }

  return (
    <div style={S.page}>
      <PublicNav />
      <div style={S.wrap}>
        <Link to="/" style={S.back}>← Alle Veranstaltungen</Link>
        {inhalt}
      </div>
    </div>
  );
}
